import * as d3 from "d3"
import { TIMELINE, TENSE, TENSE_ENUM, SEX, SEX_ENUM, POSITION_TYPE, COLOR } from "../../../modules/constants"
import { timing } from "./nexusConfig"
import { showTimelineTooltip, hideTimelineTooltip, moveTimelineTooltip } from "./timelineTooltip"
import { shortMonthNameFromNumber, monthNameFromNumber, getNameProp } from "../../../modules/utilities"
import { getRolePrefix } from "../../../components/layout/NexusTableFunctions"


const plotStartDate = new Date(`01/01/${TIMELINE.START_YEAR}`)
const plotEndDate = new Date(`01/01/${TIMELINE.END_YEAR}`)
let cursorDate = null
let cursorActive = false

//#region bars
export const createBar = props => {
  const { parent, type, height } = props

  return parent
    .append("rect") // add bars
    .attr("class", type)
    .attr("height", height)
    .attr("pointer-events", "none")
    .attr("fill", "transparent")
    .style("opacity", 0)
}

export const updateBars = props => {
  const { type, nexus, todayX } = props
  // console.log("todayX=" + todayX)
  const thisClass = `.${type}`
  const foundedX = findDateX({ date: new Date(nexus.foundedDate), plotWidth: nexus.width, foundedX: null })
  nexus.allCurrent = true

  // console.log("in updateBars")
  // return

  nexus.links.select(thisClass)
    .transition()
    .duration(timing.slow)
    .attr("x", d => {
      const date = nexus.secondLevel ? d.connectionOverlapStart : new Date(d.role.from)
      const leftX = findDateX({ date, plotWidth: nexus.width, foundedX })
      // console.log("leftX= " + leftX)

      if (d.role.tense !== TENSE_ENUM.CURRENT)
        nexus.allCurrent = false

      return type === "link-bar"
        ? leftX
        : // future-bar
        d.role.tense === TENSE_ENUM.FUTURE
          ? leftX
          : todayX
    })
    .attr("width", d => {
      const startDate = nexus.secondLevel ? d.connectionOverlapStart : new Date(d.role.from)
      const leftX = findDateX({ date: startDate, plotWidth: nexus.width, foundedX })

      // console.log("d.role.until: " + d.role.until)
      const endDate = nexus.secondLevel ? d.connectionOverlapEnd :
        d.role.until ? new Date(d.role.until) : null

      const futureRightDate = nexus.secondLevel ? d.connectionOverlapEnd : new Date(d.role.until)
      const futureRightX = findDateX({ date: futureRightDate, plotWidth: nexus.width, foundedX })

      return type === "link-bar"
        ? findBarWidth({ startDate, endDate, plotWidth: nexus.width, foundedX })

        : // future-bar
        d.role.tense === TENSE_ENUM.PAST || (nexus.secondLevel && overlapEnded({ d, nexus, foundedX, todayX })) ? 0 :
          d.role.tense === TENSE_ENUM.FUTURE
            ? Math.max(nexus.width - leftX, 0) // make sure it's not negative
            : d.role.tense === TENSE_ENUM.NOTICE
              ? Math.max(futureRightX - todayX, 0) // make sure it's not negative
              : nexus.width - todayX // current role
    })
    .attr("fill", d => type === "link-bar"
      ? nexus.secondLevel && overlapEnded({ d, nexus, foundedX, todayX })
        ? TENSE[TENSE_ENUM.PAST].COLOR
        : getTenseColor(nexus.secondLevel ? d.tense : d.role.tense)

      : // future-bar
      d.role.tense === TENSE_ENUM.NOTICE
        ? getTenseColor(d.role.tense)
        : d.role.tense === TENSE_ENUM.FUTURE
          ? "url(#horz-gradient-future)"
          : "url(#horz-gradient-current)" // current NB there are no past bars after today's date
    )
    .style("filter", d => type === "link-bar" ? null : "url(#blur)")
    .style("opacity", d => d.lseCompany
      ? TIMELINE.LSE_BAR_OPACITY
      : TIMELINE.BAR_OPACITY)
}

export const createHandler = props => {
  const { parent, handleOnClick, mobile, layout, nexus } = props
  // console.log("handleOnClick: ", handleOnClick)
  // console.log("mobile: " + mobile)

  return parent
    .append("rect") // add bars
    .attr("class", "handler")
    .attr("id", (d, i) => "handler" + i)
    .attr("height", TIMELINE.ROW_HEIGHT - TIMELINE.VERTICAL_GAP)
    .attr("fill", "black")
    .style("opacity", 0)
    .on("click", (event, d) => {
      if (mobile && layout.mobileTooltip) {                 // for mobile
        if (TIMELINE.ENABLE_TOOLTIP && d.selected !== true) {
          d.selected = true
          // showNodeTooltip(d, this.parentNode)
        } else {
          d.selected = false
          handleOnClick(d.entity)
        }
      } else {                      // for desktop
        handleOnClick(d.entity)
      }
    })
    .on('mousemove', event => {
      if (!mobile) moveCursor({ event, nexus })
      moveTimelineTooltip({ event, nexus })
    })
    .on("mouseover", (event, d, i) => {
      d3.select("#handler" + i).style("opacity", 0) // make handler bar transparent

      if (TIMELINE.ENABLE_TOOLTIP && !mobile && layout.hoverNodeTooltip
        && d.selected !== true) {
        d.selected = true
        showTimelineTooltip({ d, nexus, event })
      }
    })
    .on("mouseout", (event, d, i) => {
      if (!mobile) hideCursor(nexus)
      d3.select("#handler" + i).style("opacity", TIMELINE.HANDLER_OPACITY)

      if (TIMELINE.ENABLE_TOOLTIP && layout.hoverNodeTooltip) {
        d.selected = false
        hideTimelineTooltip()
      }
      updateName
    })
}

export const updateHandlers = nexus => {
  nexus.links.select(".handler")
    .transition()
    .duration(timing.slow)
    .attr("width", nexus.width)
    .attr("cursor", d => d.level === 0 ? "default" : "pointer")
    .style("opacity", TIMELINE.HANDLER_OPACITY)
}
//#endregion bars

//#region lines
export const createRowLine = props => {
  const { parent, type, nexus } = props
  // console.log("type: " + type)

  return parent
    .filter(d => nexus.showPosition && type === "sex" || showNedLine({ d, nexus }))
    .append("line") // add line
    .attr("class", type)
    .attr("x1", 0)
    .attr("x2", 0)
    .attr("y1", TIMELINE.ROW_HEIGHT - TIMELINE.VERTICAL_GAP)
    .attr("y2", 0)
    .attr("stroke-width", 0)
    .attr("pointer-events", "none")
    .style("opacity", 0)
}

export const updateRowLine = props => {
  const { nexus, type } = props
  const thisClass = `.${type}`

  nexus.links.select(thisClass)
    .transition()
    .duration(timing.slow)
    .attr("x1", d => type === "sex" ? 0 : getNed9X({ d, nexus }))
    .attr("x2", d => type === "sex" ? 0 : getNed9X({ d, nexus }))
    .attr("stroke", d => type === "sex"
      ? getSexColor(d.entity) // sex line
      : getNed9X({ d, nexus }) >= findDateX({ date: new Date(), plotWidth: nexus.width, foundedX: null })
        ? TENSE[TENSE_ENUM.NOTICE].COLOR // color for future 9 year ned line
        : TENSE[TENSE_ENUM.PAST].COLOR) // color for past 9 year ned line
    .attr("stroke-width", type === "sex" ? 9 : 3)
    .style("opacity", 1)
}
//#endregion lines

//#region text
export const createName = props => {
  const { parent, x, y } = props

  return parent
    .append("text") // add labels
    .attr("class", "name disable-select")
    .attr("font-size", TIMELINE.FONT_SIZE)
    .attr("x", x)
    .attr("y", y)
    .style("opacity", 0)
}

export const updateName = nexus => {
  // console.log("cursorActive: " + cursorActive)

  nexus.links.select(".name")
    .text(d => {
      const { psc, tense } = d.role
      let nodeText = getNameProp.toPresent(d.entity.name)
      const prefix = psc
      ? ` ${getRolePrefix({ kinship: d.kinship, psc, tense, parentName: nexus.name })}`
      : ", "

      if (!nexus.secondLevel) {
        nodeText += (nexus.showPosition && d.role.position) //  && (nexus.lseCompany || d.lseCompany))
          ? `${prefix}${d.role.position.title}`
          : `${prefix}${d.role.roleDescription}`
      } else { // second level
        nodeText += `, via ${getNameProp.toPresent(d.via?.name)}`
      }

      return nodeText
    })
    // .transition()
    // .duration(timing.slow)
    .style("fill", d =>
      // d.thisNode.entity && d.thisNode.entity.position && d.thisNode.entity.position.type >= 0
      //   ? "black" // POSITION[d.thisNode.entity.position.type].COLOR
      //   : "black"
      nexus.secondLevel || !cursorActive || nexus.allCurrent ||
        !TIMELINE.ENABLE_CURSOR_SELECTION || isConcurrent({ d, nexus })
        ? "black" : "lightgrey"
    )
    .attr("font-weight", d => d.entity.companyProps?.lse ||
      (TIMELINE.ENABLE_CURSOR_SELECTION && isConcurrent({ d, nexus }) && !nexus.allCurrent)
      ? "bold"
      : "normal")
    //(showPosition && (d.thisNode.entity.companyProps?.lse || getPositionFontWeight(d.thisNode.entity) === "bold")) /* || nexus.lseCompany */ ? "bold" : "normal")

    .style("opacity", 1)
}

const isConcurrent = ({ d, nexus }) => !nexus.secondLevel && cursorActive &&
  cursorDate > new Date(d.role.from) && (!d.role.until || cursorDate < new Date(d.role.until))

export const createLabel = props => {
  const { parent, y, nexus } = props

  return parent
    .filter(d => showNedLine({ d, nexus }) &&
      getNed9X({ d, nexus }) < nexus.width && getNed9X({ d, nexus }) > 0.5 * nexus.width)
    .append("text") // add labels
    .attr("class", "label disable-select")
    .attr("pointer-events", "none")
    .text("9 years")
    .style("fill", "black")
    .attr("font-size", TIMELINE.FONT_SIZE)
    .attr("text-anchor", d => getNed9X({ d, nexus }) > 0.7 * nexus.width ? "end" : "start")
    .attr("x", 0)
    .attr("y", y)
    .style("opacity", 0)
}

export const updateLabel = nexus => {
  nexus.links.select(".label")
    .transition()
    .duration(timing.slow)
    // .style("fill", "red")
    .attr("x", d => {
      const lineX = getNed9X({ d, nexus })
      return lineX > 0.7 * nexus.width ? lineX - 5 : lineX + 5
    })
    .style("opacity", 1)
}
//#endregion text

//#region vertical lines
export const createVLine = props => {
  const { parent, color, width, x, y1, y2 } = props

  return parent
    .append("line")
    .attr("class", "grid-line")
    .attr("pointer-events", "none")
    .attr("stroke", color)
    .attr("stroke-width", width)
    .attr("x1", x)
    .attr("y1", y1)
    .attr("x2", x)
    .attr("y2", y2)
    .style("opacity", 0)
}

export const createVLegend = props => {
  const { parent, text, color, x, y, align } = props

  return parent
    .append("text")
    .attr("class", "grid-text")
    .attr("pointer-events", "none")
    .attr("text-anchor", align)
    .style("fill", color)
    .text(text)
    .attr("font-size", TIMELINE.FONT_SIZE)
    .attr("font-weight", "bold")
    .attr("x", align === "start" ? x + TIMELINE.LEGEND_MARGIN : x - TIMELINE.LEGEND_MARGIN)
    .attr("y", y)
    .style("opacity", 0)
}

export const updateVLine = props => {
  const { line, x, y2, opacity } = props

  return line
    .transition()
    .duration(timing.slow)
    .attr("x1", x)
    .attr("x2", x)
    .attr("y2", y2)
    .style("opacity", opacity)
}

export const updateVLegend = props => {
  const { legend, x, opacity, align } = props

  return legend
    .transition()
    .duration(timing.slow)
    .attr("x", align === "start" ? x + TIMELINE.LEGEND_MARGIN : x - TIMELINE.LEGEND_MARGIN)
    .style("opacity", opacity)
}

export const moveCursor = ({ event, nexus }) => {
  // console.log("move cursor")
  let cursorX = event.pageX - nexus.svgX

  nexus.grid.cursor.line
    .attr("x1", cursorX)
    .attr("x2", cursorX)
    .style("opacity", 1.0)

  const yearNumber = TIMELINE.START_YEAR + ((cursorX / nexus.width) * (TIMELINE.END_YEAR - TIMELINE.START_YEAR))
  const year = Math.floor(yearNumber)
  const monthVal = 12 * (yearNumber - year)
  const monthIndex = Math.ceil(monthVal)
  const month = monthIndex > 0 && monthIndex <= 12 ? monthNameFromNumber(monthIndex) : ""
  let day = Math.ceil(30 * (1 + monthVal - monthIndex))
  if (monthIndex === 2 && day > 28) day = 28 // handle late February days

  const dateString = monthIndex > 0 && monthIndex <= 12 ? `${shortMonthNameFromNumber(monthIndex)} ${year}` : ""

  cursorDate = new Date(`${month} ${day}, ${year} 12:00:00`)
  // console.log("date: ", cursorDate)

  nexus.grid.cursor.legend
    .text(dateString)
    .attr("pointer-events", "none")
    .attr("x", cursorX + TIMELINE.LEGEND_MARGIN)
    .style("opacity", 1.0)

  cursorActive = true
  // console.log("move cursor")

  if (TIMELINE.ENABLE_CURSOR_SELECTION) updateName(nexus)
}

export const hideCursor = nexus => {
  // console.log("hide cursor")
  nexus.grid.cursor.line.style("opacity", 0)
  nexus.grid.cursor.legend.style("opacity", 0)
  cursorActive = false

  if (TIMELINE.ENABLE_CURSOR_SELECTION) updateName(nexus)
}
//#endregion vertical lines

//#region supporting functions
export const findDateX = ({ date, plotWidth, foundedX }) => {
  let x = 0

  // console.log("date: " + date)

  if (date) {
    const difference = date - plotStartDate.getTime()
    const plotDuration = plotEndDate.getTime() - plotStartDate.getTime()
    x = plotWidth * (difference / plotDuration)

    if (foundedX && x < foundedX)
      x = foundedX // corrects occasional errors at CH
  }

  return x
}

const findBarWidth = ({ startDate, endDate, plotWidth, foundedX }) => {
  const todayDate = new Date()
  const todayX = findDateX({ date: todayDate, plotWidth: plotWidth, foundedX })

  const leftX = findDateX({ date: startDate, plotWidth: plotWidth, foundedX })
  const rightX = endDate
    ? findDateX({ date: endDate, plotWidth: plotWidth, foundedX })
    : todayX

  // console.log("endDate = " + endDate)
  // console.log("rightX = " + rightX)

  let width = rightX >= todayX ? todayX - leftX : rightX - leftX
  // if (width < 0) width = 0
  if (width < 1) width = 1
  if (!width)
    console.error("invalid width value")

  // console.log("width = " + width)

  return width
}

export const sortByName = (a, b) => {
  const aSortValue = getNameProp.toSort(a.entity.name)
  const bSortValue = getNameProp.toSort(b.entity.name)

  if (aSortValue < bSortValue) return -1
  if (aSortValue > bSortValue) return 1

  return 0
}

export const sortByStart = (a, b) => {
  const aSortValue = a.role.from
  const bSortValue = b.role.from

  if (aSortValue < bSortValue) return 1
  if (aSortValue > bSortValue) return -1

  return 0
}

export const sortByDuration = (a, b) => {
  const aSortValue = a.connectionOverlapDuration
  const bSortValue = b.connectionOverlapDuration

  if (aSortValue < bSortValue) return 1
  if (aSortValue > bSortValue) return -1

  return 0
}

const getTenseColor = tense => {
  let statusColor = COLOR.BLACK
  if (tense >= 0 && tense < Object.keys(TENSE).length)
    statusColor = TENSE[tense].COLOR

  return statusColor
}

const overlapEnded = props => {
  const { d, nexus, foundedX, todayX } = props

  return d.connectionOverlapEnd &&
    (findDateX({ date: d.connectionOverlapEnd, plotWidth: nexus.width, foundedX }) < todayX - 0.1)
}

const getSexColor = entity => {
  return entity.personInfo && (entity.personInfo.sex === SEX_ENUM.MALE || entity.personInfo.sex === SEX_ENUM.FEMALE)
    ? SEX[entity.personInfo.sex].COLOR
    : "transparent" // COLOR.UNKNOWN
}

const getNed9X = ({ d, nexus }) => {
  const startX = findDateX({ date: new Date(d.role.from), plotWidth: nexus.width, foundedX: null })
  const nineYearsWidth = 9 * nexus.width / (TIMELINE.END_YEAR - TIMELINE.START_YEAR)
  let lineX = startX + nineYearsWidth

  return lineX
}

const showNedLine = ({ d, nexus }) => {
  // console.log("link: ", link)
  // console.log("position: ", d.role.position)
  return !nexus.secondLevel && nexus.showPosition && isNed(d.role.position?.type) &&
    d.role.position.indp !== 2 && // INDP[INDP_ENUM.NON_INDP] &&
    (d.role.tense === TENSE_ENUM.CURRENT || d.role.tense === TENSE_ENUM.NOTICE)
}

const isNed = positionType => positionType === POSITION_TYPE.CHAIRMAN ||
  positionType === POSITION_TYPE.SENIOR_NON_EXEC ||
  positionType === POSITION_TYPE.NON_EXEC_DIRECTOR

//#endregion supporting functions
